import React, { useState, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function RoleSelectionScreen({ send }: Props) {
  const { players, playerId, setPhase } = useGameStore();
  const [selectedRole, setSelectedRole] = useState<'customer' | 'chef' | null>(null);
  const [timeLeft, setTimeLeft] = useState(60);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Auto-select if time runs out
          if (!selectedRole) {
            handleSelectRole('customer');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [selectedRole]);

  const handleSelectRole = (role: 'customer' | 'chef') => {
    setSelectedRole(role);
    send('selectRole', { role });
  };

  const handleConfirm = () => {
    if (selectedRole) {
      // Move to character selection
      setPhase('character_selection');
    }
  };

  // Count current role selections
  const customerCount = players.filter(p => p.role === 'customer').length;
  const chefCount = players.filter(p => p.role === 'chef').length;

  return (
    <div className="screen">
      {/* Timer */}
      <div style={{
        position: 'absolute',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: timeLeft <= 10 ? 'var(--ltc-danger)' : 'var(--ltc-bg-dark)',
        color: 'var(--ltc-accent-glow)',
        padding: '8px 24px',
        border: 'var(--ltc-border)',
        fontFamily: 'var(--ltc-font-display)',
        fontSize: 20,
        animation: timeLeft <= 10 ? 'pulse 0.5s ease-in-out infinite' : 'none',
      }}>
        {timeLeft}s
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 className="title-pixel" style={{ fontSize: 24, marginBottom: 8 }}>
          Choose Your Role
        </h1>
        <p className="subtitle">What will you be today?</p>
        <p style={{ 
          color: 'var(--ltc-text-secondary)', 
          fontSize: 14, 
          marginTop: 12,
          background: 'var(--ltc-bg-secondary)',
          padding: '8px 16px',
          display: 'inline-block',
        }}>
          Players: {players.length} | Customers: {customerCount} | Chefs: {chefCount}
        </p>
      </div>

      {/* Role Cards */}
      <div style={{
        display: 'flex',
        gap: 24,
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginBottom: 32,
      }}>
        {/* Customer Role */}
        <button
          onClick={() => handleSelectRole('customer')}
          style={{
            width: 280,
            padding: 24,
            background: selectedRole === 'customer' 
              ? '#4682b4' 
              : 'var(--ltc-bg-card)',
            border: selectedRole === 'customer'
              ? '4px solid #2c5a7c'
              : '3px solid #4682b4',
            borderRadius: 15,
            boxShadow: selectedRole === 'customer'
              ? '6px 6px 0 rgba(70, 130, 180, 0.4)'
              : '0 5px 15px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: selectedRole === 'customer' ? 'translate(-2px, -2px)' : 'none',
          }}
        >
          <div 
            style={{ 
              width: 100, 
              height: 100, 
              backgroundImage: 'url(/images/characters/sprites/girl_1_idle.png)',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'left center',
              backgroundRepeat: 'no-repeat',
              imageRendering: 'pixelated',
              marginBottom: 16,
            }} 
          />
          <h2 style={{
            fontFamily: 'var(--ltc-font-body)',
            fontSize: 24,
            fontWeight: 700,
            color: selectedRole === 'customer' ? 'white' : 'var(--ltc-text-primary)',
            marginBottom: 8,
          }}>
            Customer
          </h2>
          <p style={{
            fontSize: 14,
            color: selectedRole === 'customer' ? 'rgba(255,255,255,0.9)' : 'var(--ltc-text-secondary)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Order delicious food, describe your preferences, 
            and rate your dining experience!
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              🍽️ Order Food
            </span>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              ⭐ Rate & Review
            </span>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              💰 Tip Chefs
            </span>
          </div>
          <div style={{
            marginTop: 16,
            fontSize: 12,
            color: selectedRole === 'customer' ? 'rgba(255,255,255,0.7)' : 'var(--ltc-text-muted)',
          }}>
            {customerCount} player{customerCount !== 1 ? 's' : ''} selected
          </div>
        </button>

        {/* Chef Role */}
        <button
          onClick={() => handleSelectRole('chef')}
          style={{
            width: 280,
            padding: 24,
            background: selectedRole === 'chef' 
              ? '#d4694f' 
              : 'var(--ltc-bg-card)',
            border: selectedRole === 'chef'
              ? '4px solid #a84d3a'
              : '3px solid #d4694f',
            borderRadius: 15,
            boxShadow: selectedRole === 'chef'
              ? '6px 6px 0 rgba(212, 105, 79, 0.4)'
              : '0 5px 15px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: selectedRole === 'chef' ? 'translate(-2px, -2px)' : 'none',
          }}
        >
          <div 
            style={{ 
              width: 100, 
              height: 100, 
              backgroundImage: 'url(/images/characters/sprites/chibi_wizard_idle.png)',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'left center',
              backgroundRepeat: 'no-repeat',
              imageRendering: 'pixelated',
              marginBottom: 16,
            }} 
          />
          <h2 style={{
            fontFamily: 'var(--ltc-font-body)',
            fontSize: 24,
            fontWeight: 700,
            color: selectedRole === 'chef' ? 'white' : 'var(--ltc-text-primary)',
            marginBottom: 8,
          }}>
            Chef
          </h2>
          <p style={{
            fontSize: 14,
            color: selectedRole === 'chef' ? 'rgba(255,255,255,0.9)' : 'var(--ltc-text-secondary)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Cook delicious meals, manage ingredients, 
            and satisfy hungry customers!
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              🍳 Cook Food
            </span>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              📦 Manage Stock
            </span>
            <span style={{
              background: 'var(--ltc-bg-secondary)',
              padding: '4px 8px',
              fontSize: 12,
              color: 'var(--ltc-text-primary)',
            }}>
              ⏱️ Race Clock
            </span>
          </div>
          <div style={{
            marginTop: 16,
            fontSize: 12,
            color: selectedRole === 'chef' ? 'rgba(255,255,255,0.7)' : 'var(--ltc-text-muted)',
          }}>
            {chefCount} player{chefCount !== 1 ? 's' : ''} selected
          </div>
        </button>
      </div>

      {/* Confirm Button */}
      {selectedRole && (
        <button
          className="btn btn-accent btn-large"
          onClick={handleConfirm}
          style={{ minWidth: 200 }}
        >
          Continue as {selectedRole === 'customer' ? 'Customer' : 'Chef'} →
        </button>
      )}

      {/* Tip */}
      <p style={{
        marginTop: 24,
        color: 'var(--ltc-text-muted)',
        fontSize: 14,
        textAlign: 'center',
      }}>
        💡 Tip: A good balance of customers and chefs makes the game more fun!
      </p>
    </div>
  );
}
